import type * as Highcharts from 'highcharts'

import {
  KpiDashboardItem,
  KpiDashboardKeyAction,
  KpiOperatorKeyActionMonthlyTrend,
  KpiOperatorKeyActionDailyTrendResult,
  KpiOperatorKeyActionTrendWindow,
  KpiOperatorMetric,
  KpiOperatorMonthlyMetrics,
} from '../../../services/kpi-dashboard/kpi-dashboard.service'
import {
  MetricGapClass,
  formatGap,
  getGapClass,
} from '../operator-list/operator-list.logic'

/** 目標未設定時に指標カードへ表示する文言。 */
const TARGET_UNSET_TEXT = '目標未設定'
const TREND_COLORS = ['#2563eb', '#059669', '#ea580c', '#7c3aed']
const KEY_ACTION_TREND_COLORS = ['#0e7490', '#b45309', '#7e22ce']
const KEY_ACTION_TREND_MARKERS = ['circle', 'square', 'triangle']

export interface OperatorMetricViewModel {
  itemId: string
  label: string
  numerator: number | null
  denominator: number | null
  targetRate: number | null
  actualRate: number | null
  gap: number | null
  actualRateText: string
  targetText: string
  gapText: string
  formulaText: string
  achievementText: string
  gapClass: MetricGapClass
}

export interface TrendChartViewModel {
  itemId: string
  label: string
  options: Highcharts.Options
}

export interface KeyActionTrendViewModel {
  rangeText: string
  emptyText: string
  options: Highcharts.Options | null
}

export interface KeyActionDailyTrendViewModel extends KeyActionTrendViewModel {
  period: string
}

/** オペレーター KPI の分子・分母から実績率を算出する。 */
export function calculateOperatorActualRate(
  metric: KpiOperatorMetric,
): number | null {
  assertMetricValues(metric)
  if (
    metric.numerator == null ||
    metric.denominator == null ||
    metric.denominator === 0
  ) {
    return null
  }

  return (metric.numerator / metric.denominator) * 100
}

/** オペレーター KPI の実績率と目標率の差を算出する。 */
export function calculateOperatorGap(metric: KpiOperatorMetric): number | null {
  assertMetricValues(metric)
  const actualRate = calculateOperatorActualRate(metric)
  if (actualRate == null || metric.targetRate == null) {
    return null
  }

  return actualRate - metric.targetRate
}

/** KPI 項目を定義順の実績表示モデルに変換する。 */
export function buildOperatorMetricViewModels(
  items: readonly KpiDashboardItem[],
  metrics: readonly KpiOperatorMetric[],
): OperatorMetricViewModel[] {
  return [...items]
    .sort((left, right) => left.sortOrder - right.sortOrder)
    .map((item) => {
      const metric = getOperatorMetric(metrics, item.id)
      assertMetricValues(metric)
      const actualRate = calculateOperatorActualRate(metric)
      const gap = calculateOperatorGap(metric)

      return {
        itemId: item.id,
        label: item.kpiName,
        numerator: metric.numerator,
        denominator: metric.denominator,
        targetRate: metric.targetRate,
        actualRate,
        gap,
        actualRateText: formatRateValue(actualRate),
        targetText:
          metric.targetRate == null
            ? TARGET_UNSET_TEXT
            : `目標 ${formatRateValue(metric.targetRate)}%`,
        gapText: gap == null ? '' : formatGap(gap),
        formulaText: formatFormula(metric, item.denominatorName),
        achievementText: gap == null ? '' : gap >= 0 ? '達成' : '未達',
        gapClass: getGapClass(gap),
      }
    })
}

/** KPI 項目を定義順の空実績表示モデルに変換する。 */
export function buildEmptyOperatorMetricViewModels(
  items: readonly KpiDashboardItem[],
): OperatorMetricViewModel[] {
  return [...items]
    .sort((left, right) => left.sortOrder - right.sortOrder)
    .map((item) => ({
      itemId: item.id,
      label: item.kpiName,
      numerator: null,
      denominator: null,
      targetRate: null,
      actualRate: null,
      gap: null,
      actualRateText: '-',
      targetText: TARGET_UNSET_TEXT,
      gapText: '',
      formulaText: '-',
      achievementText: '',
      gapClass: 'neutral',
    }))
}

/** YYYY-MM 形式の期間を年月表記に整形する。 */
export function formatPeriod(period: string): string {
  const { year, month } = parsePeriod(period)
  return `${year}年${month}月`
}

/** 月次推移の先頭月と年が切り替わる月だけ年を含むラベルに整形する。 */
export function formatTrendPeriodLabels(periods: readonly string[]): string[] {
  let previousYear: number | null = null
  return periods.map((period) => {
    const { year, month } = parsePeriod(period)
    const includeYear = previousYear == null || year !== previousYear
    previousYear = year
    return includeYear ? `${year}/${month}` : `${month}月`
  })
}

/** KPI 項目を定義順の月次推移チャート表示モデルに変換する。 */
export function buildTrendChartViewModels(
  items: readonly KpiDashboardItem[],
  monthlyTrend: readonly KpiOperatorMonthlyMetrics[],
): TrendChartViewModel[] {
  return [...items]
    .sort((left, right) => left.sortOrder - right.sortOrder)
    .map((item, index) => ({
      itemId: item.id,
      label: item.kpiName,
      options: createTrendChartOptions(item, monthlyTrend, index),
    }))
}

/** 月次推移チャートの Highcharts オプションを生成する。 */
export function createTrendChartOptions(
  item: KpiDashboardItem,
  monthlyTrend: readonly KpiOperatorMonthlyMetrics[],
  colorIndex: number,
): Highcharts.Options {
  const actualColor = TREND_COLORS[colorIndex % TREND_COLORS.length]
  const orderedMonthlyTrend = orderMonthlyTrend(monthlyTrend)
  const metrics = orderedMonthlyTrend.map((entry) =>
    getOperatorMetric(entry.metrics, item.id),
  )

  const baseOptions = createTrendChartBaseOptions(
    formatTrendPeriodLabels(orderedMonthlyTrend.map(({ period }) => period)),
    `${item.kpiName}の月次推移`,
  )
  return {
    ...baseOptions,
    accessibility: { enabled: true },
    chart: { ...baseOptions.chart, height: 250 },
    legend: {
      ...baseOptions.legend,
      align: 'right',
      verticalAlign: 'top',
    },
    yAxis: {
      ...baseOptions.yAxis,
      min: 0,
      labels: { ...baseOptions.yAxis.labels, format: '{value}%' },
    },
    tooltip: {
      shared: true,
      valueDecimals: 1,
      valueSuffix: '%',
    },
    series: [
      {
        type: 'line',
        name: '実績',
        color: actualColor,
        lineWidth: 3,
        marker: {
          enabled: true,
          radius: 4,
          fillColor: '#fff',
          lineColor: actualColor,
          lineWidth: 2,
        },
        dataLabels: {
          enabled: true,
          color: actualColor,
          style: { fontSize: '10px', textOutline: 'none' },
          formatter() {
            return typeof this.y === 'number' ? `${this.y.toFixed(1)}%` : ''
          },
        },
        data: metrics.map((metric) => calculateOperatorActualRate(metric)),
      },
      {
        type: 'line',
        name: '目標',
        color: '#64748b',
        dashStyle: 'Dash',
        lineWidth: 2,
        marker: { enabled: true, radius: 3 },
        data: metrics.map((metric) => {
          if (typeof metric.targetRate === 'undefined') {
            throw new Error(`KPI target rate is missing: ${metric.itemId}`)
          }
          return metric.targetRate
        }),
      },
    ],
  }
}

/** YYYY-MM の両端を含む月を昇順で列挙し、不正な形式・逆転した期間を拒否する。 */
export function enumeratePeriods(
  periodFrom: string,
  periodTo: string,
): string[] {
  const from = parsePeriod(periodFrom)
  const to = parsePeriod(periodTo)
  const start = from.year * 12 + from.month - 1
  const end = to.year * 12 + to.month - 1
  if (start > end) {
    throw new Error('KeyAction trend window is reversed')
  }
  const periods: string[] = []
  for (let month = start; month <= end; month++) {
    periods.push(formatMonthIndex(month))
  }
  return periods
}

/** 保存済み重点 KA をカタログ順に解決し、API の集計窓内の評価から表示モデルを生成する。 */
export function buildKeyActionTrendViewModel(
  keyActions: readonly KpiDashboardKeyAction[],
  selectedKeyActionIds: readonly string[],
  keyActionMonthlyTrend: readonly KpiOperatorKeyActionMonthlyTrend[],
  window: KpiOperatorKeyActionTrendWindow,
): KeyActionTrendViewModel {
  const periods = enumeratePeriods(window.periodFrom, window.periodTo)
  const rangeText = createKeyActionTrendRangeText(periods)
  const emptyText = '重点強化 KeyAction の評価済みアセスメントがありません。'
  const selectedKeyActions = resolveSelectedKeyActions(
    keyActions,
    selectedKeyActionIds,
  )
  return {
    rangeText,
    emptyText,
    options: createKeyActionTrendChartOptions(
      selectedKeyActions,
      keyActionMonthlyTrend.map(({ period, scores }) => ({
        key: period,
        scores,
      })),
      periods,
      formatTrendPeriodLabels(periods),
      rangeText,
      '重点強化 KeyAction スコアの月次推移',
    ),
  }
}

/** 保存済み重点 KA をカタログ順で解決し、存在しない ID を拒否する。 */
function resolveSelectedKeyActions(
  keyActions: readonly KpiDashboardKeyAction[],
  selectedKeyActionIds: readonly string[],
): KpiDashboardKeyAction[] {
  for (const id of selectedKeyActionIds) {
    if (!keyActions.some((keyAction) => keyAction.id === id)) {
      throw new Error(`KeyAction is missing: ${id}`)
    }
  }
  return keyActions.filter(({ id }) => selectedKeyActionIds.includes(id))
}

/** 日次集計の全日軸を作り、当日より後の商談日も表示範囲に含める。 */
export function buildKeyActionDailyTrendViewModel(
  keyActions: readonly KpiDashboardKeyAction[],
  selectedKeyActionIds: readonly string[],
  result: KpiOperatorKeyActionDailyTrendResult,
): KeyActionDailyTrendViewModel {
  const { dateFrom, dateTo } = result.window
  // 延長前の窓も検証し、不正な期間を未来日の評価で隠さない。
  enumerateDates(dateFrom, dateTo)
  if (result.period !== dateFrom.slice(0, 7)) {
    throw new Error('KeyAction daily trend period does not match window')
  }
  let axisEnd = dateTo
  for (const entry of result.dailyTrend) {
    parseDate(entry.date)
    if (entry.date < dateFrom) {
      throw new Error('KeyAction daily trend date is before window')
    }
    if (entry.date > axisEnd) axisEnd = entry.date
  }
  const dates = enumerateDates(dateFrom, axisEnd)
  const rangeText = `${formatPeriod(result.period)}（${formatDate(dateFrom)}〜${formatDate(axisEnd)}）・商談日ベースの日次平均（ツールチップに評価件数を表示）`
  const options = createKeyActionTrendChartOptions(
    resolveSelectedKeyActions(keyActions, selectedKeyActionIds),
    result.dailyTrend.map(({ date, scores }) => ({ key: date, scores })),
    dates,
    formatDailyTrendDateLabels(dates),
    rangeText,
    '重点強化 KeyAction スコアの日次推移',
  )
  return {
    period: result.period,
    rangeText,
    emptyText:
      'この月に重点強化 KeyAction の評価済みアセスメントがありません。',
    options,
  }
}

/** 集計窓の月・日軸に、欠測を補間せず件数付き平均スコアを表示する。 */
function createKeyActionTrendChartOptions(
  keyActions: readonly KpiDashboardKeyAction[],
  trend: readonly {
    key: string
    scores: KpiOperatorKeyActionMonthlyTrend['scores']
  }[],
  keys: readonly string[],
  labels: readonly string[],
  rangeText: string,
  containerLabel: string,
): Highcharts.Options | null {
  const scoresByKey = new Map(
    trend.map(({ key, scores }) => [
      key,
      new Map(scores.map((score) => [score.keyActionId, score])),
    ]),
  )
  const series = keyActions.map((keyAction, index) => {
    const color =
      KEY_ACTION_TREND_COLORS[index % KEY_ACTION_TREND_COLORS.length]
    return {
      type: 'line' as const,
      name: keyAction.name,
      color,
      lineWidth: 3,
      marker: {
        symbol:
          KEY_ACTION_TREND_MARKERS[index % KEY_ACTION_TREND_MARKERS.length],
        enabled: true,
        radius: 4,
        fillColor: '#fff',
        lineColor: color,
        lineWidth: 2,
      },
      dataLabels: { enabled: false },
      data: keys.map((key) => {
        const score = scoresByKey.get(key)?.get(keyAction.id)
        if (score == null) {
          return null
        }
        if (
          !Number.isFinite(score.averageScore) ||
          !Number.isInteger(score.assessmentCount) ||
          score.assessmentCount < 1
        ) {
          throw new Error(
            'KeyAction monthly score or assessment count is invalid',
          )
        }
        return {
          y: score.averageScore,
          custom: { assessmentCount: score.assessmentCount },
        }
      }),
    }
  })
  if (!series.some((s) => s.data.some((d) => d != null))) {
    return null
  }
  const baseOptions = createTrendChartBaseOptions(labels, containerLabel)
  return {
    ...baseOptions,
    accessibility: { enabled: true, description: rangeText },
    chart: { ...baseOptions.chart, height: 300 },
    legend: {
      ...baseOptions.legend,
      align: 'center',
      verticalAlign: 'bottom',
    },
    yAxis: {
      ...baseOptions.yAxis,
      min: 0,
      softMax: 5,
      tickInterval: 1,
      labels: { ...baseOptions.yAxis.labels, format: '{value}点' },
    },
    tooltip: {
      shared: true,
      /** 平均スコアと、その母数である評価件数を表示する。 */
      pointFormatter() {
        const y = this.y
        if (typeof y !== 'number' || !Number.isFinite(y)) {
          throw new Error('KeyAction monthly average score is invalid')
        }
        const assessmentCount = this.options.custom?.['assessmentCount']
        if (
          typeof assessmentCount !== 'number' ||
          !Number.isInteger(assessmentCount) ||
          assessmentCount < 1
        ) {
          throw new Error('KeyAction monthly assessment count is invalid')
        }
        return `${escapeHtml(this.series.name)}: ${y.toFixed(1)} 点（n=${assessmentCount}）<br/>`
      },
    },
    series,
  }
}

/** KPI と重点 KA の折れ線チャートで共通の描画・軸・凡例設定を生成する。 */
function createTrendChartBaseOptions(
  labels: readonly string[],
  containerLabel: string,
) {
  return {
    lang: { accessibility: { chartContainerLabel: containerLabel } },
    chart: {
      type: 'line',
      backgroundColor: 'transparent',
      spacing: [12, 8, 4, 4],
    },
    title: { text: undefined },
    credits: { enabled: false },
    xAxis: {
      categories: [...labels],
      tickLength: 0,
      lineColor: '#cbd5e1',
      labels: { style: { color: '#64748b', fontSize: '10px' } },
    },
    yAxis: {
      title: { text: undefined },
      gridLineColor: '#e2e8f0',
      labels: { style: { color: '#64748b' } },
    },
    plotOptions: {
      series: { animation: false, connectNulls: false },
    },
    legend: { itemStyle: { color: '#475569', fontSize: '10px' } },
  } satisfies Highcharts.Options
}

/** ツールチップに表示する系列名を HTML として解釈されない文字列にする。 */
function escapeHtml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;')
}

/** 開始月と終了月を年月表記の期間に整形する。 */
function formatPeriodRange(from: string, to: string): string {
  return `${formatPeriod(from)}〜${formatPeriod(to)}`
}

/** 検証済みの月軸から集計期間・月数と集計方法のキャプションを生成する。 */
export function createKeyActionTrendRangeText(
  periods: readonly string[],
): string {
  if (periods.length === 0) {
    throw new Error('KeyAction trend periods are empty')
  }
  return `${formatPeriodRange(periods[0], periods[periods.length - 1])}（直近 ${periods.length} か月）・商談日ベースの月次平均（ツールチップに評価件数を表示）`
}

/** 月次推移の期間レンジを年月表記で生成する。 */
export function createTrendRangeText(
  monthlyTrend: readonly KpiOperatorMonthlyMetrics[],
): string | null {
  if (monthlyTrend.length === 0) {
    return null
  }

  const orderedMonthlyTrend = orderMonthlyTrend(monthlyTrend)
  return `KPI 実績 ${formatPeriodRange(
    orderedMonthlyTrend[0].period,
    orderedMonthlyTrend[orderedMonthlyTrend.length - 1].period,
  )}`
}

/** オペレーター名からアバター用の先頭 2 文字を生成する。 */
export function createAvatarText(name: string): string {
  const normalizedName = name.replace(/\s/g, '')
  if (normalizedName === '') {
    throw new Error('Operator name is empty')
  }

  return normalizedName.slice(0, 2)
}

/** 指定 KPI 項目に対応するオペレーター実績を取得する。 */
export function getOperatorMetric(
  metrics: readonly KpiOperatorMetric[],
  itemId: string,
): KpiOperatorMetric {
  const metric = metrics.find((candidate) => candidate.itemId === itemId)
  if (metric == null) {
    throw new Error(`KPI metric is missing: ${itemId}`)
  }

  return metric
}

function assertMetricValues(
  metric: KpiOperatorMetric,
): asserts metric is KpiOperatorMetric & {
  numerator: number | null
  denominator: number | null
  targetRate: number | null
} {
  if (typeof metric.numerator === 'undefined') {
    throw new Error(`KPI numerator is missing: ${metric.itemId}`)
  }
  if (typeof metric.denominator === 'undefined') {
    throw new Error(`KPI denominator is missing: ${metric.itemId}`)
  }
  if (typeof metric.targetRate === 'undefined') {
    throw new Error(`KPI target rate is missing: ${metric.itemId}`)
  }
}

function formatRateValue(rate: number | null): string {
  return rate == null ? '-' : rate.toFixed(1)
}

function formatFormula(
  metric: KpiOperatorMetric,
  denominatorName: string,
): string {
  if (metric.numerator == null || metric.denominator == null) {
    return '-'
  }

  return `${metric.numerator}件 ÷ ${metric.denominator}件（${denominatorName}）`
}

function orderMonthlyTrend(
  monthlyTrend: readonly KpiOperatorMonthlyMetrics[],
): KpiOperatorMonthlyMetrics[] {
  return [...monthlyTrend].sort((left, right) =>
    left.period.localeCompare(right.period),
  )
}

function parsePeriod(period: string): { year: number; month: number } {
  const match = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(period)
  if (match == null) {
    throw new Error(`KPI period is invalid: ${period}`)
  }

  return { year: Number(match[1]), month: Number(match[2]) }
}

/** YYYY-MM を指定月数だけ移動する。 */
export function shiftPeriod(period: string, delta: number): string {
  const { year, month } = parsePeriod(period)
  if (!Number.isInteger(delta)) throw new Error('KPI period delta is invalid')
  return formatMonthIndex(year * 12 + month - 1 + delta)
}

/** 年月を表す通算月を YYYY-MM に戻す。 */
function formatMonthIndex(month: number): string {
  if (month < 0 || month >= 10000 * 12)
    throw new Error('KPI period is out of range')
  return `${String(Math.floor(month / 12)).padStart(4, '0')}-${String((month % 12) + 1).padStart(2, '0')}`
}

/** YYYY-MM-DD の両端を含む日付を UTC で列挙する。 */
export function enumerateDates(dateFrom: string, dateTo: string): string[] {
  const current = parseDate(dateFrom)
  const end = parseDate(dateTo)
  if (current > end) throw new Error('KeyAction daily trend window is reversed')
  const dates: string[] = []
  while (current <= end) {
    dates.push(current.toISOString().slice(0, 10))
    current.setUTCDate(current.getUTCDate() + 1)
  }
  return dates
}

/** 日付を月日表記に整形する。 */
export function formatDate(date: string): string {
  const parsed = parseDate(date)
  return `${parsed.getUTCMonth() + 1}月${parsed.getUTCDate()}日`
}

/** 日次軸のラベルを月日表記にする。密度は Highcharts に委ねる。 */
export function formatDailyTrendDateLabels(dates: readonly string[]): string[] {
  return dates.map((date) => {
    const parsed = parseDate(date)
    return `${parsed.getUTCMonth() + 1}/${parsed.getUTCDate()}`
  })
}

/** 日付の形式と暦上の実在を検証する。 */
function parseDate(date: string): Date {
  if (!/^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/.test(date)) {
    throw new Error(`KPI date is invalid: ${date}`)
  }
  const parsed = new Date(`${date}T00:00:00.000Z`)
  if (
    !Number.isFinite(parsed.getTime()) ||
    parsed.toISOString().slice(0, 10) !== date
  ) {
    throw new Error(`KPI date is invalid: ${date}`)
  }
  return parsed
}
